import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API, downloadReceipt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Truck, LogOut, Inbox, Loader2, Users, FileText, Trash2, ChevronDown,
  Mail, Phone, MapPin, Calendar, IdCard, CreditCard, DollarSign, Route, Download,
} from "lucide-react";

const Q_STATUS = {
  new: { label: "New", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { label: "In Progress", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  completed: { label: "Completed", cls: "bg-green-50 text-green-700 border-green-200" },
};
const D_STATUS = {
  new: { label: "New", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  contacted: { label: "Contacted", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  hired: { label: "Hired", cls: "bg-green-50 text-green-700 border-green-200" },
};

function Metric({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-500">{label}</span>
        <span className={`grid place-items-center w-9 h-9 rounded-lg ${accent}`}><Icon className="w-4 h-4" /></span>
      </div>
      <div className="mt-3 font-display font-extrabold text-3xl text-ksy-navy">{value}</div>
    </div>
  );
}

function Badge({ map, status }) {
  const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full border ${s.cls}`}>{s.label}</span>;
}

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({ quotes_total: 0, quotes_new: 0, drivers_total: 0, drivers_new: 0, bookings_total: 0, bookings_paid: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const [q, d, b, p, s] = await Promise.all([api.get("/quotes"), api.get("/drivers"), api.get("/bookings"), api.get("/partners"), api.get("/stats")]);
      setQuotes(q.data); setDrivers(d.data); setBookings(b.data); setPartners(p.data); setStats(s.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = async (bookingId, partner_email) => {
    try { await api.patch(`/bookings/${bookingId}/assign`, { partner_email: partner_email || null }); toast.success(partner_email ? "Driver assigned" : "Unassigned"); load(); }
    catch (e) { toast.error("Assign failed"); }
  };

  const [downloadingId, setDownloadingId] = useState(null);
  const receipt = async (id) => {
    setDownloadingId(id);
    try { await downloadReceipt(id); }
    catch { toast.error("Could not download receipt"); }
    finally { setDownloadingId(null); }
  };

  const changeStatus = async (type, id, status) => {
    try { await api.patch(`/${type}/${id}`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error("Update failed"); }
  };
  const remove = async (type, id) => {
    try { await api.delete(`/${type}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const onLogout = async () => { await logout(); navigate("/admin/login"); };

  const list = tab === "quotes" ? quotes : drivers;

  return (
    <div className="min-h-screen flex bg-ksy-bg">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 fixed h-full">
        <div className="p-5 border-b border-slate-200 flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-ksy-navy text-white"><Truck className="w-5 h-5" /></span>
          <div>
            <div className="font-display font-extrabold text-ksy-navy leading-tight">KS&amp;Y Dispatch</div>
            <div className="text-xs text-slate-400">Admin panel</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <button onClick={() => { setTab("quotes"); setExpanded(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors ${tab === "quotes" ? "bg-blue-50 text-ksy-blue" : "text-slate-600 hover:bg-slate-50"}`} data-testid="tab-quotes">
            <FileText className="w-4 h-4" /> Quote Requests
            <span className="ml-auto text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{stats.quotes_total}</span>
          </button>
          <button onClick={() => { setTab("drivers"); setExpanded(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors ${tab === "drivers" ? "bg-blue-50 text-ksy-blue" : "text-slate-600 hover:bg-slate-50"}`} data-testid="tab-drivers">
            <Users className="w-4 h-4" /> Driver Applications
            <span className="ml-auto text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{stats.drivers_total}</span>
          </button>
          <button onClick={() => { setTab("bookings"); setExpanded(null); }} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors ${tab === "bookings" ? "bg-blue-50 text-ksy-blue" : "text-slate-600 hover:bg-slate-50"}`} data-testid="tab-bookings">
            <CreditCard className="w-4 h-4" /> Paid Bookings
            <span className="ml-auto text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{stats.bookings_total}</span>
          </button>
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="px-4 py-2">
            <div className="text-xs text-slate-400">Signed in as</div>
            <div className="text-sm font-semibold text-ksy-navy truncate">{user?.email}</div>
          </div>
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-50 font-semibold text-sm" data-testid="logout-button">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 p-5 md:p-8 ksy-scroll">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-extrabold text-2xl md:text-3xl text-ksy-navy">Dashboard</h1>
            <p className="text-slate-500">Manage quote requests and driver applications.</p>
          </div>
          <button onClick={onLogout} className="md:hidden text-slate-600" data-testid="mobile-logout"><LogOut className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Metric icon={FileText} label="Total quotes" value={stats.quotes_total} accent="bg-blue-50 text-ksy-blue" testid="metric-quotes-total" />
          <Metric icon={Users} label="Total drivers" value={stats.drivers_total} accent="bg-blue-50 text-ksy-blue" testid="metric-drivers-total" />
          <Metric icon={CreditCard} label="Paid bookings" value={stats.bookings_paid} accent="bg-green-50 text-green-600" testid="metric-bookings-paid" />
          <Metric icon={DollarSign} label="Revenue" value={`$${Number(stats.revenue || 0).toLocaleString()}`} accent="bg-green-50 text-green-600" testid="metric-revenue" />
        </div>

        {/* Mobile tab switch */}
        <div className="md:hidden flex gap-2 mb-4">
          <button onClick={() => setTab("quotes")} className={`flex-1 py-2 rounded-lg font-semibold text-xs ${tab === "quotes" ? "bg-ksy-blue text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="mtab-quotes">Quotes</button>
          <button onClick={() => setTab("drivers")} className={`flex-1 py-2 rounded-lg font-semibold text-xs ${tab === "drivers" ? "bg-ksy-blue text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="mtab-drivers">Drivers</button>
          <button onClick={() => setTab("bookings")} className={`flex-1 py-2 rounded-lg font-semibold text-xs ${tab === "bookings" ? "bg-ksy-blue text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="mtab-bookings">Bookings</button>
        </div>

        {tab === "bookings" ? (
          loading ? (
            <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 text-ksy-blue animate-spin" /></div>
          ) : bookings.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-16 text-center" data-testid="empty-state">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No bookings yet.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden" data-testid="bookings-list">
              {bookings.map((b) => (
                <div key={b.id} data-testid={`bookings-row-${b.id}`}>
                  <button onClick={() => setExpanded(expanded === b.id ? null : b.id)} className="w-full grid grid-cols-12 gap-3 items-center px-5 py-4 hover:bg-slate-50 transition-colors text-left">
                    <div className="col-span-6 md:col-span-3">
                      <div className="font-semibold text-ksy-navy">{b.full_name}</div>
                      <div className="text-xs text-slate-400">{new Date(b.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="hidden md:block md:col-span-4 text-sm text-slate-500 truncate">
                      {b.origin} → {b.destination}
                      {b.assigned_partner_name && <span className="ml-2 text-xs font-semibold text-ksy-blue">· {b.assigned_partner_name}</span>}
                    </div>
                    <div className="hidden md:block md:col-span-2 text-sm font-semibold text-ksy-navy">${Number(b.total).toLocaleString()}</div>
                    <div className="col-span-4 md:col-span-2">
                      <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full border ${b.payment_status === "paid" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {b.payment_status === "paid" ? "Paid" : "Pending"}
                      </span>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex justify-end"><ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded === b.id ? "rotate-180" : ""}`} /></div>
                  </button>
                  {expanded === b.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="overflow-hidden bg-slate-50 border-t border-slate-100" data-testid={`bookings-detail-${b.id}`}>
                      <div className="px-5 py-5 grid md:grid-cols-3 gap-5 text-sm">
                        <div className="space-y-2.5">
                          <a href={`mailto:${b.email}`} className="flex items-center gap-2 text-slate-600 hover:text-ksy-blue"><Mail className="w-4 h-4" />{b.email}</a>
                          <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-ksy-blue"><Phone className="w-4 h-4" />{b.phone}</a>
                          {b.pickup_date && <div className="flex items-center gap-2 text-slate-600"><Calendar className="w-4 h-4" />Pickup: {b.pickup_date} {b.max_delivery_days ? `· ≤${b.max_delivery_days}d` : ""}</div>}
                        </div>
                        <div className="space-y-2.5">
                          <div className="flex items-center gap-2 text-slate-600"><Route className="w-4 h-4 text-ksy-blue" />{Number(b.miles).toLocaleString()} mi · {b.transport_type}</div>
                          <div className="flex items-center gap-2 text-slate-600"><Truck className="w-4 h-4" />{b.num_cars} vehicle(s) · ${Number(b.price_per_car).toLocaleString()}/ea</div>
                          {b.pickup_address && <div className="text-slate-500">From: {b.pickup_address}</div>}
                          {b.delivery_address && <div className="text-slate-500">To: {b.delivery_address}</div>}
                        </div>
                        <div className="flex flex-col justify-between">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Total paid</div>
                            <div className="font-display font-extrabold text-2xl text-green-600">${Number(b.total).toLocaleString()}</div>
                            {b.payment_status === "paid" && (
                              <button
                                onClick={() => receipt(b.id)}
                                disabled={downloadingId === b.id}
                                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-ksy-blue border border-ksy-blue/30 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                                data-testid={`download-receipt-${b.id}`}
                              >
                                {downloadingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                Download receipt
                              </button>
                            )}
                          </div>
                          <div className="mt-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Assigned driver</div>
                            <select
                              value={b.assigned_partner_email || ""}
                              onChange={(e) => assign(b.id, e.target.value)}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-ksy-navy focus:border-ksy-blue focus:outline-none"
                              data-testid={`assign-${b.id}`}
                            >
                              <option value="">— Unassigned —</option>
                              {partners.map((p) => <option key={p.id} value={p.email}>{p.name} ({p.email})</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : loading ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 text-ksy-blue animate-spin" /></div>
        ) : list.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-16 text-center" data-testid="empty-state">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No {tab === "quotes" ? "quote requests" : "driver applications"} yet.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden" data-testid={`${tab}-list`}>
            {list.map((item) => {
              const isQ = tab === "quotes";
              const map = isQ ? Q_STATUS : D_STATUS;
              const statuses = Object.keys(map);
              return (
                <div key={item.id} data-testid={`${tab}-row-${item.id}`}>
                  <button onClick={() => setExpanded(expanded === item.id ? null : item.id)} className="w-full grid grid-cols-12 gap-3 items-center px-5 py-4 hover:bg-slate-50 transition-colors text-left">
                    <div className="col-span-6 md:col-span-3">
                      <div className="font-semibold text-ksy-navy">{item.full_name}</div>
                      <div className="text-xs text-slate-400">{new Date(item.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="hidden md:block md:col-span-4 text-sm text-slate-500 truncate">
                      {isQ ? `${item.origin} → ${item.destination}` : `${item.city || "—"}, ${item.state || "—"}`}
                    </div>
                    <div className="hidden md:block md:col-span-2 text-sm text-slate-500">
                      {isQ ? item.transport_type : item.cdl_experience}
                    </div>
                    <div className="col-span-4 md:col-span-2"><Badge map={map} status={item.status} /></div>
                    <div className="col-span-2 md:col-span-1 flex justify-end">
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded === item.id ? "rotate-180" : ""}`} />
                    </div>
                  </button>

                  {expanded === item.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="overflow-hidden bg-slate-50 border-t border-slate-100" data-testid={`${tab}-detail-${item.id}`}>
                      <div className="px-5 py-5 grid md:grid-cols-3 gap-5">
                        <div className="space-y-2.5 text-sm">
                          <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-slate-600 hover:text-ksy-blue"><Mail className="w-4 h-4" />{item.email}</a>
                          <a href={`tel:${item.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-ksy-blue"><Phone className="w-4 h-4" />{item.phone}</a>
                          {isQ && item.ship_date && <div className="flex items-center gap-2 text-slate-600"><Calendar className="w-4 h-4" />{item.ship_date}</div>}
                          {!isQ && <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4" />{item.city || "—"}, {item.state || "—"}</div>}
                        </div>
                        <div className="space-y-2.5 text-sm">
                          {isQ ? (
                            <>
                              <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-ksy-blue" />{item.origin} → {item.destination}</div>
                              <div className="flex items-center gap-2 text-slate-600"><Truck className="w-4 h-4" />{item.vehicle_year} {item.vehicle_make} {item.vehicle_model}</div>
                              {item.notes && <p className="text-slate-500 italic">"{item.notes}"</p>}
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2 text-slate-600"><IdCard className="w-4 h-4 text-ksy-blue" />Experience: {item.cdl_experience}</div>
                              {item.endorsements && <div className="text-slate-600">Endorsements: {item.endorsements}</div>}
                              {item.license_path ? (
                                <a href={`${API}/drivers/${item.id}/license`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-semibold text-ksy-blue hover:underline" data-testid={`license-link-${item.id}`}>
                                  <FileText className="w-4 h-4" /> View CDL / license
                                </a>
                              ) : (
                                <div className="text-slate-400">No license uploaded</div>
                              )}
                              {item.message && <p className="text-slate-500 italic">"{item.message}"</p>}
                            </>
                          )}
                        </div>
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Update status</div>
                          <div className="flex flex-wrap gap-2">
                            {statuses.map((s) => (
                              <button key={s} onClick={() => changeStatus(tab, item.id, s)} disabled={item.status === s} className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors ${item.status === s ? "bg-ksy-navy text-white border-ksy-navy" : "bg-white text-slate-600 border-slate-200 hover:border-ksy-blue hover:text-ksy-blue"}`} data-testid={`set-status-${s}-${item.id}`}>
                                {map[s].label}
                              </button>
                            ))}
                          </div>
                          <button onClick={() => remove(tab, item.id)} className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-red-500 hover:text-red-600" data-testid={`delete-${item.id}`}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
